/** The Huddles area's hooks and helpers (core HuddlesViewModel): the sidebar, the dialogs and the screens share them. */
import { Shell, Sidebar, type HuddleDestination, type HuddlesViewModel, type OpenTarget, type WorkspaceCache } from "@bundle/core";
import { useSyncExternalStore } from "react";
import { useHuddle } from "../context/HuddleContext.tsx";
import type { Translate } from "../i18n/I18nProvider.tsx";
import { openLink } from "../primitives/openLink.ts";
import { useWorkspace } from "./index.ts";

const noSubscribe = () => () => {};

/** The area's view model and state (null where calls are off: no flag, or Huddles switched off for me). */
export function useHuddlesArea(): { vm: HuddlesViewModel; state: HuddlesViewModel["state"] } | null {
  const huddle = useHuddle();
  const vm = huddle?.area;
  const state = useSyncExternalStore(vm?.subscribe ?? noSubscribe, () => vm?.state);
  if (vm === undefined || state === undefined) return null;
  return { vm, state };
}

/** "YYYY-MM-DD" of today, local. */
export function todayIso(now = Date.now()): string {
  const d = new Date(now);
  const p = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
}

/** A huddle's name: the link's title, the conversation's name ("# design", "Bo, Cy"), or "Huddle". */
export function huddleTitle(cache: WorkspaceCache | undefined, channelId: string, linkTitle: string | undefined, t: Translate): string {
  if (linkTitle !== undefined && linkTitle !== "") return linkTitle;
  const c = cache?.channels[channelId];
  if (c === undefined || cache === undefined) return t("huddles.untitled");
  const name = Sidebar.channelTitle(cache, c.id);
  return c.kind === "dm" || c.kind === "group_dm" ? name : `# ${name}`;
}

/** Follows where a join leads: the call's tab, or a link page in a new browser tab. */
export function useFollow(open: (url: string, target?: OpenTarget) => void): (d: HuddleDestination | undefined) => void {
  const ws = useWorkspace();
  const huddle = useHuddle();
  return (d) => {
    if (d === undefined) return;
    if (d.kind === "page") {
      openLink(d.url);
      return;
    }
    if (huddle?.browserOnly === true) {
      huddle.openInBrowser(d.channelKey);
      return;
    }
    open(Shell.formatUrl({ type: "huddle", ws: ws.slug, huddleId: d.channelKey }));
  };
}
